import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    BarChart3,
    Cake,
    Calendar,
    Download,
    Gift,
    Heart,
    Inbox,
    Search,
    Send,
    ShoppingCart,
    UserMinus,
    UserPlus,
    Users,
    Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogInfo from '../../component/dialog/DialogInfo';
import AnimatedNumber from '../../component/common/AnimatedNumber';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { KvHero, KvStripItem, UsageKpi } from '../../component/report/KvKpis';
import DefaultTable from '../../component/table/DefaultTable';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileReport, { MobileReportBody, useMobileRegenerate } from '../../component/report/MobileReport';
import { AreaLine, ChartCard, DonutBlock, HBarsLimited } from '../../component/report/MobileSalesSections';
import { KvCard, PagedRows, RowValue, StatTiles } from '../../component/report/MobileListSections';
import { KitField, KitInput, KitSeg } from '../../component/common/MobileKitExtras';
import { KitPill } from '../../component/common/MobileKit';
import { KitEmptyNote } from '../../component/common/MobileDashboardKit';

/**
 * Cobertura de dados x protótipo (`prototipo-relatorio-clientes.html`):
 * `POST /chart/customer` (`report/customer.go`) não devolve vários campos
 * que o protótipo assume por linha/registro — `customer_type` (PF/PJ),
 * contagem de vendas/OS por cliente, data da última interação, telefone e
 * data de nascimento dos aniversariantes, nem uma série mensal de
 * "clientes inativados". Nada disso é inventado aqui: cada lacuna está
 * comentada no trecho correspondente, com o que a tela usa como
 * aproximação (sempre derivada de campos realmente devolvidos pela API) e o
 * que precisaria mudar no backend para fechar 1:1 com o protótipo. Extensão
 * de backend não foi implementada nesta tarefa — é só frontend.
 */

const FULL_MONTH_LABELS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MONTH_ABBR_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const parseApiNumber = (v) => {
    const n = Number(String(v ?? '0').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};

const formatBrl = (v) =>
    parseApiNumber(v).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const normalizeText = (s) =>
    String(s ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();

// Parser de `ListInactiveCustomers` (`report/customer.go`) — a API devolve
// uma string já pronta ("Nome (inativo a mais de X meses)") em vez de um
// objeto estruturado; ver comentário em `inactiveCustomers` (useMemo) sobre
// as limitações disso.
const INACTIVE_LABEL_RE = /^(.*)\s\(inativo a mais de (\d+)\s*mes(?:es)?\)$/i;

const TB_FIELD_RADIUS = '9px';
// Busca embutida no cabeçalho do card "Total gasto por cliente".
const textFieldSx = {
    width: { xs: '100%', sm: 260 },
    flexShrink: 0,
    '& .MuiOutlinedInput-root': {
        height: 40,
        fontSize: '13px',
        borderRadius: TB_FIELD_RADIUS,
        backgroundColor: 'background.subtle',
    },
};

// `.stat-tile` — ícone circular + valor grande + legenda, usado no card
// "Resumo de Relevância".
function StatTile({ icon, iconBg, iconFg, value, label, loading }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1, p: '16px 8px', borderRadius: '12px', bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 42, height: 42, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: iconBg, color: iconFg }}>
                {icon}
            </Box>
            {loading ? (
                <Skeleton variant="text" width={40} height={30} />
            ) : (
                <Typography sx={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary' }}>{value}</Typography>
            )}
            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</Typography>
        </Box>
    );
}

// `.metric-row` — ícone quadrado tintado + rótulo pequeno + valor em negrito.
function MetricRow({ icon, iconBg, iconFg, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '11px 10px', borderRadius: '10px', '&:hover': { bgcolor: 'background.subtle' } }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: iconBg, color: iconFg }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled' }}>{label}</Typography>
                <Typography sx={{ fontSize: '14.5px', fontWeight: 800, color: 'text.primary' }}>{value}</Typography>
            </Box>
        </Box>
    );
}

// `.chip-count` — contador arredondado usado nas colunas de quantidade.
function CountChip({ value }) {
    return (
        <Chip
            label={value}
            size="small"
            sx={{ minWidth: 30, height: 22, fontWeight: 800, fontSize: '12px', bgcolor: 'info.bg', color: 'primary.main' }}
        />
    );
}

// `.pill` — dot + rótulo, usado para severidade (dias sem interação).
const PILL_TONE_SX = {
    green: { bgcolor: 'success.bg', color: 'success.main' },
    amber: { bgcolor: 'warning.bg', color: 'warning.main' },
    red: { bgcolor: 'error.bg', color: 'error.main' },
    blue: { bgcolor: 'info.bg', color: 'primary.main' },
    purple: { bgcolor: 'secondary.bg', color: 'secondary.main' },
    grey: { bgcolor: 'background.subtle', color: 'text.secondary' },
};
function Pill({ label, tone = 'grey' }) {
    const toneSx = PILL_TONE_SX[tone] || PILL_TONE_SX.grey;
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', ...toneSx }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
            {label}
        </Box>
    );
}

// DESIGN-SYSTEM.md §5.11 empty state (variante compacta) — mesmo componente
// local de `SalesReport.js`/`SaleDashboard.js`/`OrderDashboard.js`.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// `.legend-item` — dot + rótulo + valor em negrito, usado sob os donuts.
function LegendRow({ color, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11.5px', color: 'text.secondary', width: '100%' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.primary', ml: 'auto' }}>{value}</Typography>
        </Box>
    );
}

// Painel de gráfico/card genérico (`.panel-head`), mesmo componente local
// de `SalesReport.js`.
function ChartPanel({ title, subtitle, headerActions, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                        {loading ? (
                            <>
                                <Skeleton variant="text" width={160} height={20} />
                                <Skeleton variant="text" width={200} height={14} />
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                                {subtitle ? (
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography>
                                ) : null}
                            </>
                        )}
                    </Box>
                    {headerActions ? <Box sx={{ flexShrink: 0 }}>{headerActions}</Box> : null}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {children}
                </Box>
            </CardContent>
        </Card>
    );
}

// Donut (PieChart real via @mui/x-charts) com overlay central + legenda —
// mesmo padrão de `SalesReport.js`/`SaleDashboard.js`.
function DonutPanel({ title, subtitle, data, centerValue, centerCaption, loading, emptyTitle, emptySubtitle }) {
    const hasData = data.some((item) => item.value > 0);
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Skeleton variant="circular" width={128} height={128} />
                    <Skeleton variant="text" width="80%" height={16} />
                    <Skeleton variant="text" width="70%" height={16} />
                </Box>
            ) : hasData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ position: 'relative', width: 128, height: 128 }}>
                        <PieChart
                            series={[{
                                data,
                                innerRadius: 38,
                                outerRadius: 64,
                                paddingAngle: 2,
                                cornerRadius: 3,
                            }]}
                            height={128}
                            width={128}
                            slotProps={{ legend: { hidden: true } }}
                            tooltip={{ trigger: 'item' }}
                            sx={{ '& .MuiChartsLegend-root': { display: 'none' } }}
                        />
                        <Box sx={{ position: 'absolute', inset: 18, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>{centerValue}</Typography>
                            <Typography sx={{ fontSize: '9.5px', color: 'text.disabled' }}>{centerCaption}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                        {data.map((item, index) => (
                            <LegendRow key={`${item.label}-${index}`} color={item.color} label={item.label} value={item.displayValue ?? item.value} />
                        ))}
                    </Box>
                </Box>
            ) : (
                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
            )}
        </ChartPanel>
    );
}

// Gráfico de linha/área — DESIGN-SYSTEM.md §5.9 "Inline SVG polyline +
// gradient area | LineChart (area variant)", mesma técnica de `SalesReport.js`.
function AreaLineChart({ dataset, xKey, yKey, gradientId, title, subtitle, loading, height = 260, valueSuffix = '' }) {
    const theme = useTheme();
    const hasData = Array.isArray(dataset) && dataset.length > 0;
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : hasData ? (
                    <LineChart
                        dataset={dataset}
                        height={height - 20}
                        margin={{ top: 16, right: 16, bottom: 24, left: 8 }}
                        hideLegend
                        grid={{ horizontal: true }}
                        xAxis={[{
                            scaleType: 'point',
                            dataKey: xKey,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{ disableLine: true, disableTicks: true }]}
                        series={[{
                            dataKey: yKey,
                            label: title,
                            color: theme.palette.primary.main,
                            area: true,
                            showMark: dataset.length <= 31,
                            valueFormatter: (value) => `${Number(value || 0).toLocaleString('pt-BR')}${valueSuffix}`,
                        }]}
                        sx={{
                            '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                            '& .MuiAreaElement-root': { fill: `url(#${gradientId})` },
                            '& .MuiMarkElement-root': { fill: theme.palette.background.paper, stroke: theme.palette.primary.main, strokeWidth: 2 },
                        }}
                    >
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                    </LineChart>
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem clientes cadastrados no ano" />
                )}
            </Box>
        </ChartPanel>
    );
}

// Gráfico de barras genérico (contagem por categoria) — mesmo padrão de
// `SalesReport.js`.
function CategoryBarChart({ data, xKey, title, subtitle, loading, height = 240 }) {
    const theme = useTheme();
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : data && data.length > 0 ? (
                    <BarChart
                        dataset={data}
                        height={height - 20}
                        margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                        borderRadius={6}
                        hideLegend
                        xAxis={[{
                            scaleType: 'band',
                            dataKey: xKey,
                            categoryGapRatio: 0.5,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{ disableLine: true, disableTicks: true }]}
                        series={[{
                            dataKey: 'count',
                            color: theme.palette.primary.main,
                            valueFormatter: (value) => `${value} cliente(s)`,
                        }]}
                        sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                    />
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem clientes com valores no período" />
                )}
            </Box>
        </ChartPanel>
    );
}

// `MiniTable` — tabela compacta sem paginação/busca própria (`DefaultTable`
// tem overhead de paginação/skeleton pensado pra listas grandes; as tabelas
// secundárias desta tela — aniversariantes, inativos, rankings top 5 — são
// pequenas e não paginadas no protótipo, então usam markup de tabela direto,
// só com o mesmo estilo de cabeçalho/linha do design system).
const miniHeadCellSx = { py: 1.25, px: 1.5, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', borderBottom: '1px solid', borderColor: 'divider' };
const miniBodyCellSx = { py: 1.25, px: 1.5, fontSize: '12.5px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' };
function MiniTable({ columns, rows, renderRow, maxHeight = 320, emptyTitle, emptySubtitle }) {
    if (!rows || rows.length === 0) {
        return <EmptyMiniState icon={<Inbox size={22} strokeWidth={2} />} title={emptyTitle || 'Nenhum registro'} subtitle={emptySubtitle || 'Sem dados para exibir'} />;
    }
    return (
        <TableContainer sx={{ maxHeight, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.key} align={col.align || 'left'} sx={{ ...miniHeadCellSx, bgcolor: 'background.paper' }}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => renderRow(row, index))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function CustomerReport() {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [search, setSearch] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');

    // Filtros (mobile): volta aos valores padrão sem gerar o relatório.
    const handleClearFilters = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
        setSearch('');
    };

    const fetchReportData = async () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        setLoading(true);
        try {
            const response = await httpClient.post('/chart/customer', {
                start_date: startDate.format('DD/MM/YYYY'),
                end_date: endDate.format('DD/MM/YYYY')
            });

            setReportData(response.data);
        } catch (error) {
            console.error('Erro ao buscar dados do relatório:', error);
            setDialogMessage('Erro ao gerar relatório. Tente novamente.');
            setOpenDialog(true);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        const url = `/chart/customers/export?start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`;
        window.open(url, '_blank');
    };

    // ------- Linhas "Total de Vendas & OS" (por cliente) -------
    // `AmountSaleAndOrders` só traz nome + valor de vendas + valor de OS por
    // cliente (`CustomerAmountData` em `report/customer.go`) — sem
    // `customer_type` (PF/PJ), sem contagem de vendas/OS e sem data da
    // última interação, os quatro campos que o protótipo mostra por linha
    // além do valor. A tabela abaixo usa só o que a API devolve; as colunas
    // sem dado (Tipo/Qtd. vendas/Qtd. OS/Última interação) ficam de fora em
    // vez de inventadas — ver relato final da tarefa para o que precisaria
    // no backend para fechar 1:1 com o protótipo.
    const customerRows = useMemo(() => {
        const list = reportData?.AmountSaleAndOrders ?? [];
        if (!Array.isArray(list)) return [];
        return list.map((row) => {
            const sales = parseApiNumber(row.total_sales_value_reais);
            const os = parseApiNumber(row.total_order_service_value);
            return {
                id: row.customer_id,
                name: row.name,
                sales,
                os,
                total: sales + os,
            };
        }).sort((a, b) => b.total - a.total);
    }, [reportData]);

    const filteredCustomerRows = useMemo(() => {
        const term = normalizeText(search);
        if (!term) return customerRows;
        return customerRows.filter((row) => normalizeText(row.name).includes(term));
    }, [customerRows, search]);

    // Ticket médio por cliente cadastrado = (vendas + OS de todos os
    // clientes) / total de clientes cadastrados — derivado 100% de campos já
    // devolvidos pela API (`AmountSaleAndOrders` + `CountCustomers`), sem
    // endpoint novo.
    const totalSpent = useMemo(() => customerRows.reduce((sum, row) => sum + row.total, 0), [customerRows]);
    const countCustomers = reportData?.CountCustomers || 0;
    const averageTicket = countCustomers > 0 ? totalSpent / countCustomers : 0;

    // Perfil PF / PJ — `CustomersByAgeRange` filtra só `customer_type = 'pf'`
    // (soma dos valores = total de clientes PF); PJ é a diferença para
    // `CountCustomers`. É uma aproximação (a query de faixa etária não
    // filtra `active`, `CountCustomers` filtra) — mais precisa exigiria o
    // backend devolver a contagem PF/PJ diretamente.
    const pfCount = useMemo(() => {
        const values = reportData?.CustomersByAgeRange?.Values ?? [];
        return values.reduce((sum, v) => sum + parseApiNumber(v), 0);
    }, [reportData]);
    const pjCount = Math.max(0, countCustomers - pfCount);
    const pfPct = countCustomers > 0 ? Math.round((pfCount / countCustomers) * 100) : 0;
    const pjPct = countCustomers > 0 ? 100 - pfPct : 0;

    const birthdays = useMemo(() => reportData?.Birthdays ?? [], [reportData]);
    const filteredBirthdays = useMemo(() => {
        const term = normalizeText(search);
        if (!term) return birthdays;
        return birthdays.filter((b) => normalizeText(b.name).includes(term));
    }, [birthdays, search]);

    // Clientes inativos — `ListInactiveCustomers` devolve só uma string
    // pronta ("Nome (inativo a mais de X meses)"), sem tipo, sem data exata
    // da última interação e sem valor gasto. O parser abaixo extrai
    // nome/meses do texto (única forma estruturada disponível) e cruza com
    // `customerRows` pelo nome pra tentar recuperar o total histórico
    // gasto — quando não encontra, mostra "—" em vez de inventar valor.
    const inactiveCustomers = useMemo(() => {
        const list = reportData?.ListInactiveCustomers ?? [];
        return list
            .map((raw, index) => {
                const match = INACTIVE_LABEL_RE.exec(raw || '');
                const parsedName = match ? match[1].trim() : (raw || '').trim();
                const name = parsedName || 'Cliente sem nome cadastrado';
                const months = match ? Number(match[2]) : null;
                const approxDays = months != null ? months * 30 : null;
                const spendRow = customerRows.find((row) => normalizeText(row.name) === normalizeText(name));
                return {
                    id: `inactive-${index}`,
                    name,
                    months,
                    approxDays,
                    totalSpent: spendRow ? spendRow.total : null,
                };
            })
            // A API devolve em ordem alfabética (`ORDER BY c.full_name ASC` em
            // `getListInactiveCustomers`) — reordenado aqui por dias sem
            // interação (decrescente) pra cumprir "priorize contato pelos mais
            // antigos" (mesmo texto do protótipo), sem precisar de outro campo.
            .sort((a, b) => (b.approxDays ?? -1) - (a.approxDays ?? -1));
    }, [reportData, customerRows]);

    const filteredInactiveCustomers = useMemo(() => {
        const term = normalizeText(search);
        if (!term) return inactiveCustomers;
        return inactiveCustomers.filter((c) => normalizeText(c.name).includes(term));
    }, [inactiveCustomers, search]);

    // Aniversariantes também inativos — cruzamento por nome entre os dois
    // conjuntos já devolvidos pela API (`Birthdays` x `ListInactiveCustomers`
    // parseado acima), sem chamada extra.
    const birthdaysAlsoInactiveCount = useMemo(() => {
        const inactiveNames = new Set(inactiveCustomers.map((c) => normalizeText(c.name)));
        return birthdays.filter((b) => inactiveNames.has(normalizeText(b.name))).length;
    }, [birthdays, inactiveCustomers]);

    // Ativos x Inativos — mesma base (`active = true`) usada por
    // `CountCustomers` e `InactiveCustomers` no backend.
    const activeCount = Math.max(0, countCustomers - (reportData?.InactiveCustomers || 0));
    const activePct = countCustomers > 0 ? Math.round((activeCount / countCustomers) * 100) : 0;
    const inactiveCount = reportData?.InactiveCustomers || 0;
    const inactivePct = countCustomers > 0 ? Math.min(100, Math.round((inactiveCount / countCustomers) * 1000) / 10) : 0;

    // Crescimento mensal da base — `MonthlyGrowthCustomers` (Jan a Dez do
    // ano corrente, não cumulativo).
    const monthlyGrowthData = useMemo(() => {
        const block = reportData?.MonthlyGrowthCustomers;
        const values = block?.Values ?? [];
        return MONTH_ABBR_LABELS.map((label, index) => ({ month: label, total: parseApiNumber(values[index]) }));
    }, [reportData]);

    // Clientes por faixa de gasto — histograma calculado no cliente a partir
    // do total (vendas + OS) por cliente já devolvido pela API, sem endpoint novo.
    const spendBrackets = useMemo(() => {
        const buckets = [
            { label: 'R$ 0–100', min: 0, max: 100 },
            { label: 'R$ 100–500', min: 100, max: 500 },
            { label: 'R$ 500–1.000', min: 500, max: 1000 },
            { label: 'R$ 1.000+', min: 1000, max: Infinity },
        ];
        return buckets.map((bucket) => ({
            faixa: bucket.label,
            count: customerRows.filter((row) => row.total >= bucket.min && (bucket.max === Infinity ? true : row.total < bucket.max)).length,
        }));
    }, [customerRows]);

    const top5BySpend = useMemo(() => customerRows.slice(0, 5), [customerRows]);

    // Top 5 por frequência de vendas — reaproveita `CountSalesByCustomers`
    // (já ordenado por quantidade no backend). É só vendas finalizadas, não
    // "vendas + OS" como no protótipo, porque não há contagem de OS por
    // cliente na API hoje.
    const top5BySalesCount = useMemo(() => {
        const block = reportData?.CountSalesByCustomers;
        const labels = block?.Labels ?? [];
        const values = block?.Values ?? [];
        return labels
            .map((label, index) => ({ id: `freq-${index}`, name: label, count: parseApiNumber(values[index]) }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [reportData]);

    const currentMonthLabel = `${FULL_MONTH_LABELS[dayjs().month()]} de ${dayjs().year()}`;

    const handleTabChange = (tab) => setActiveTab(tab);

    const isMobileShell = useIsMobileShell();
    // Mobile: gera ao abrir e a cada troca de período/filtros (mesma chamada e payload do botão "Gerar").
    const regenerate = useMobileRegenerate(fetchReportData, { enabled: isMobileShell });

    if (isMobileShell) {
        const inactive = reportData?.InactiveCustomers || 0;
        const loyalPct = countCustomers > 0 ? Math.round(((reportData?.CountLoyalCustomers || 0) / countCustomers) * 100) : 0;
        const newCustomers = reportData?.NewCustomers || 0;
        const brl = (v) => formatBrl(v);
        const segmentsOf = (data) => data.map((item) => ({ value: item.value, color: item.color }));
        const legendOf = (data) => data.map((item) => ({ label: `${item.label} · ${item.displayValue ?? item.value.toLocaleString('pt-BR')}`, color: item.color }));
        const growthItems = monthlyGrowthData.map((r) => ({ label: r.month, value: r.total }));
        const peakGrowth = growthItems.reduce((best, it) => (it.value > (best?.value ?? -1) ? it : best), null);
        const profileData = [
            { label: 'Pessoa Física', value: pfCount, displayValue: `${pfPct}%`, color: theme.palette.primary.main },
            { label: 'Pessoa Jurídica', value: pjCount, displayValue: `${pjPct}%`, color: theme.palette.secondary.main },
        ];
        const activeData = [
            { label: 'Ativos', value: activeCount, color: theme.palette.success.main },
            { label: 'Inativos', value: inactive, color: theme.palette.error.main },
        ];
        const maxBracket = spendBrackets.reduce((m, b) => Math.max(m, b.count), 0);

        const salesRows = filteredCustomerRows.map((row) => ({
            key: row.id,
            title: row.name,
            sub: `Vendas ${brl(row.sales)} · OS ${brl(row.os)}`,
            end: <RowValue color="success.main">{brl(row.total)}</RowValue>,
        }));
        const birthdayRows = filteredBirthdays.map((row, index) => ({
            key: `${row.name}-${index}`,
            title: row.name,
            end: <RowValue>{row.age} anos</RowValue>,
        }));
        const inactiveRows = filteredInactiveCustomers.map((row) => ({
            key: row.id,
            title: row.name,
            sub: `Total histórico gasto: ${row.totalSpent != null ? brl(row.totalSpent) : '—'}`,
            end: row.approxDays != null
                ? <KitPill tone={row.approxDays >= 180 ? 'danger' : 'warning'}>{`~${row.approxDays} dias`}</KitPill>
                : <Box sx={{ fontSize: 12, color: 'text.disabled' }}>—</Box>,
        }));

        const tabNode = activeTab === 0 ? (
            <>
                <ChartCard title="Total gasto por cliente" subtitle="Vendas + ordens de serviço no período, ordenado por valor total" bare>
                    <PagedRows key={`${filteredCustomerRows.length}-${search}`} rows={salesRows} empty="Nenhum cliente encontrado" noun="clientes" />
                </ChartCard>
            </>
        ) : activeTab === 1 ? (
            <>
                <ChartCard title="Aniversariantes do Mês" subtitle={`${currentMonthLabel} — oportunidade de contato e mensagem de parabéns`} bare>
                    <PagedRows key={`b-${filteredBirthdays.length}-${search}`} rows={birthdayRows} empty="Nenhum aniversariante neste mês" noun="aniversariantes" />
                </ChartCard>
                <ChartCard title="Resumo de Relevância" subtitle="Clientes que merecem atenção no período" bare>
                    <StatTiles
                        items={[
                            { label: 'Aniversariantes', value: birthdays.length.toLocaleString('pt-BR') },
                            { label: 'Inativos', value: inactive.toLocaleString('pt-BR') },
                            { label: 'Novos no período', value: newCustomers.toLocaleString('pt-BR') },
                        ]}
                    />
                    <Box sx={{ height: 10 }} />
                    <KvCard
                        rows={[
                            { label: 'Aniversariantes também inativos', value: `${birthdaysAlsoInactiveCount} cliente(s) — ótima oportunidade de reengajamento` },
                            { label: 'Critério de inatividade', value: 'Cadastro com mais de 60 dias e nenhuma venda finalizada ou OS paga até hoje' },
                        ]}
                    />
                </ChartCard>
                <ChartCard title="Clientes Inativos" subtitle="Sem venda finalizada ou OS paga registrada — priorize contato pelos mais antigos" bare>
                    <PagedRows key={`i-${filteredInactiveCustomers.length}-${search}`} rows={inactiveRows} empty="Todos os clientes tiveram interação recente" noun="clientes" />
                </ChartCard>
            </>
        ) : (
            <>
                <ChartCard title="Perfil de clientes" subtitle="Pessoa Física x Pessoa Jurídica (estimado)" bare>
                    <DonutBlock segments={segmentsOf(profileData)} center={countCustomers.toLocaleString('pt-BR')} legend={legendOf(profileData)} empty="Sem clientes cadastrados" />
                </ChartCard>
                <ChartCard title="Crescimento da base de clientes" subtitle={`Total de clientes cadastrados por mês em ${dayjs().year()}`}>
                    {monthlyGrowthData.length > 0 ? (
                        <>
                            <AreaLine items={growthItems} />
                            {peakGrowth && peakGrowth.value > 0 && (
                                <Box sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4, mt: '10px' }}>
                                    Maior mês: <strong>{peakGrowth.label}</strong> · {peakGrowth.value.toLocaleString('pt-BR')} cliente(s)
                                </Box>
                            )}
                        </>
                    ) : <KitEmptyNote>Sem clientes cadastrados no ano</KitEmptyNote>}
                </ChartCard>
                <ChartCard title="Ativos x Inativos" bare>
                    <DonutBlock segments={segmentsOf(activeData)} center={`${activePct}%`} legend={legendOf(activeData)} empty="Sem clientes cadastrados" />
                </ChartCard>
                <ChartCard title="Clientes por faixa de gasto">
                    {maxBracket > 0 ? (
                        <HBarsLimited items={spendBrackets.map((b) => ({ name: b.faixa, value: b.count, text: String(b.count) }))} valueWidth={40} />
                    ) : <KitEmptyNote>Sem clientes com valores no período</KitEmptyNote>}
                </ChartCard>
                <ChartCard title="Clientes com maior valor gasto" subtitle="Top 5 no período (vendas + OS)" bare>
                    <PagedRows
                        paged={false}
                        rows={top5BySpend.map((row, i) => ({ key: row.id, avatar: { text: String(i + 1), tone: 'soft' }, title: row.name, end: <RowValue color="success.main">{brl(row.total)}</RowValue> }))}
                        empty="Sem clientes com valores no período"
                    />
                </ChartCard>
                <ChartCard title="Clientes com mais vendas" subtitle="Top 5 por quantidade de vendas finalizadas" bare>
                    <PagedRows
                        paged={false}
                        rows={top5BySalesCount.map((row, i) => ({ key: row.id, avatar: { text: String(i + 1), tone: 'soft' }, title: row.name, end: <RowValue>{row.count.toLocaleString('pt-BR')}</RowValue> }))}
                        empty="Sem vendas finalizadas no período"
                    />
                </ChartCard>
            </>
        );

        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitSeg
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[{ value: 0, label: 'Vendas & OS' }, { value: 1, label: 'Relevantes' }, { value: 2, label: 'Gráficos' }]}
                />
                <Box sx={{ mt: '14px' }}>{tabNode}</Box>
            </Box>
        );

        return (
            <PageScaffold title="Relatório de Clientes" titlePageTarget="Clientes" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <MobileReport
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onGenerate={regenerate}
                    onClear={handleClearFilters}
                    extraFilters={(
                        <KitField label="Buscar">
                            <KitInput type="search" aria-label="Buscar cliente" placeholder="Nome do cliente" value={search} onChange={(e) => setSearch(e.target.value)} />
                        </KitField>
                    )}
                >
                    <MobileReportBody
                        loading={loading}
                        ready={Boolean(reportData)}
                        kpis={[
                            { label: 'Clientes', icon: 'users', value: countCustomers.toLocaleString('pt-BR'), delta: { text: newCustomers > 0 ? `+${newCustomers} no período` : '', tone: 'up' } },
                            { label: 'Ativos', icon: 'check', grad: true, value: Math.max(0, countCustomers - inactive).toLocaleString('pt-BR') },
                            { label: 'Recorrentes', icon: 'refresh', value: `${loyalPct}%` },
                            { label: 'Inativos', icon: 'pause', value: inactive.toLocaleString('pt-BR'), delta: { text: '', tone: 'down' } },
                        ]}
                        sections={[
                            {
                                type: 'kpis',
                                title: 'Indicadores gerais da base',
                                items: [
                                    { label: 'Novos no período', icon: 'users', value: newCustomers.toLocaleString('pt-BR') },
                                    { label: 'Ticket médio', icon: 'wallet', value: formatBrl(averageTicket) },
                                    { label: 'Perfil (PF / PJ)', icon: 'user', value: `${pfPct}% / ${pjPct}%` },
                                    { label: 'Aniversariantes', icon: 'calendar', value: birthdays.length.toLocaleString('pt-BR') },
                                    { label: 'Valor total gerado', icon: 'dollar', value: formatBrl(totalSpent) },
                                ],
                            },
                            { type: 'node', title: 'Detalhamento', node: tabsNode },
                        ]}
                    />
                </MobileReport>
                <DialogInfo open={openDialog} onClose={() => setOpenDialog(false)} title="Erro" iconType="error" content={dialogMessage} />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Relatório de Clientes"
            titlePageTarget="Clientes"
            links={[{ title: "Relatórios", link: "/reports" }]}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* page-head — ícone + título + descrição + ação primária (Exportar CSV). */}
                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                                <Users size={18} strokeWidth={2} />
                            </Box>
                            <Box>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    Relatório de Clientes
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                    Base de clientes cadastrada, consumo em vendas e ordens de serviço, e oportunidades de relacionamento.
                                </Typography>
                            </Box>
                        </Box>

                        {/* <Button variant="contained" startIcon={<Download size={16} strokeWidth={2} />} onClick={handleExport}>
                            Exportar CSV
                        </Button> */}
                    </Box>

                    {/* report-toolbar — período + busca + gerar relatório.
                        Sem os campos "Tipo" (PF/PJ) e "Status" do protótipo:
                        nenhuma linha devolvida por `/chart/customer` traz
                        `customer_type` ou um status por cliente hoje, então um
                        select que não filtra nada de verdade ficaria enganoso —
                        preferimos deixar de fora a ter um controle decorativo.
                        Ver relato final para o que o backend precisaria expor. */}
                    <ReportFilterBar
                        start={startDate}
                        end={endDate}
                        onPeriodChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onGenerate={fetchReportData}
                        loading={loading}
                        onClear={handleClearFilters}
                    />

                    {/* Chips de contexto — período + totais reais devolvidos pela API. */}
                    <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                        <Chip
                            icon={<Calendar size={13} strokeWidth={2} />}
                            label={`Período: ${startDate?.format('DD/MM/YYYY')} – ${endDate?.format('DD/MM/YYYY')}`}
                            size="small"
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                        {reportData && (
                            <>
                                <Chip icon={<Users size={13} strokeWidth={2} />} label={`${countCustomers.toLocaleString('pt-BR')} clientes cadastrados`} size="small" sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                                <Chip icon={<UserPlus size={13} strokeWidth={2} />} label={`${(reportData.NewCustomers || 0).toLocaleString('pt-BR')} novos no período`} size="small" sx={{ bgcolor: 'success.bg', color: 'success.main' }} />
                                <Chip icon={<UserMinus size={13} strokeWidth={2} />} label={`${(reportData.InactiveCustomers || 0).toLocaleString('pt-BR')} clientes inativos`} size="small" sx={{ bgcolor: 'warning.bg', color: 'warning.main' }} />
                            </>
                        )}
                    </FilterChips>

                    {reportData ? (
                        <>
                            {/* KPI grid (`.kv-grid`) — hero cadastrados + inativos/ativos,
                                novos e ticket médio. Sem delta de "novos" nem sparkline
                                fake: `NewCustomers` não tem período anterior na API; a
                                sparkline usa `MonthlyGrowthCustomers` (cadastros/mês do
                                ano corrente). */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '2fr 1fr 1fr' }, gap: { xs: 1, md: '14px' }, mb: { xs: 1.5, md: '18px' } }}>
                                <KvHero
                                    loading={loading}
                                    main={{
                                        icon: <Users size={17} strokeWidth={2} />,
                                        iconBg: 'info.bg',
                                        iconFg: 'primary.main',
                                        label: 'Clientes cadastrados',
                                        labelTitle: 'Total de clientes na base',
                                        value: <AnimatedNumber value={countCustomers} />,
                                        sub: monthlyGrowthData.some((r) => r.total > 0) ? 'Cadastros por mês no ano corrente' : 'Base total de clientes',
                                        growth: monthlyGrowthData.map((r) => r.total),
                                    }}
                                    side={{
                                        title: 'Distribuição ativos x inativos',
                                        icon: <UserMinus size={17} strokeWidth={2} />,
                                        iconBg: 'error.bg',
                                        iconFg: 'error.main',
                                        label: 'Clientes inativos',
                                        labelTitle: 'Clientes ativos sem venda/OS finalizada recente e cadastrados há mais de 60 dias',
                                        value: <AnimatedNumber value={inactiveCount} />,
                                        pct: <><AnimatedNumber value={inactivePct} decimals={1} format={(n) => n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} />%</>,
                                        pctTone: 'error',
                                        barPct: inactivePct,
                                        barBg: 'success.main',
                                        barFg: 'error.main',
                                        legend: [
                                            { color: 'success.main', text: `${activeCount.toLocaleString('pt-BR')} ativos` },
                                            { color: 'error.main', text: `${inactiveCount.toLocaleString('pt-BR')} inativos` },
                                        ],
                                        hint: 'Ativos = cadastrados − inativos',
                                    }}
                                />
                                <UsageKpi icon={<UserPlus size={17} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" tag="Aquisição" value={<AnimatedNumber value={reportData.NewCustomers || 0} />} label="Novos clientes no período" hint="Cadastros ativos nos últimos 30 dias" loading={loading} />
                                <UsageKpi icon={<Wallet size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main" tag="Receita" value={<AnimatedNumber value={averageTicket} decimals={2} format={formatBrl} />} label="Ticket médio por cliente" hint="Valor total gerado (vendas + OS) ÷ clientes cadastrados" loading={loading} />
                            </Box>

                            {/* Indicadores gerais da base (`.kv-strip`) — 3º indicador
                                (frequência média de retorno) não existe na API hoje;
                                mantido "valor total gerado", derivado 100% de campos já
                                devolvidos (soma de `AmountSaleAndOrders`). */}
                            <Typography sx={{ fontSize: '12px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, m: '2px 0 10px' }}>
                                Indicadores gerais da base
                            </Typography>
                            <Card sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: { xs: 1.5, md: '18px' } }}>
                                <KvStripItem icon={<Users size={17} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" value={<><AnimatedNumber value={pfPct} format={String} />% / <AnimatedNumber value={pjPct} format={String} />%</>} label="Perfil de clientes (PF / PJ)" loading={loading}>
                                    <Box title={`PF ${pfPct}% · PJ ${pjPct}%`} sx={{ height: 6, borderRadius: '20px', bgcolor: 'primary.main', overflow: 'hidden', mt: '6px' }}>
                                        <Box sx={{ height: '100%', width: `${pjPct}%`, bgcolor: 'secondary.main', borderRadius: '20px' }} />
                                    </Box>
                                </KvStripItem>
                                <KvStripItem icon={<Gift size={17} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" value={<AnimatedNumber value={birthdays.length} />} label="Aniversariantes no mês" loading={loading} />
                                <KvStripItem icon={<Wallet size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" value={<AnimatedNumber value={totalSpent} decimals={2} format={formatBrl} />} label="Valor total gerado (vendas + OS)" loading={loading} />
                            </Card>

                            {/* seg-tabs — DESIGN-SYSTEM.md §5.4, mesmo `ToggleButtonGroup`
                                restilizado globalmente em `theme.js`, com medidas locais
                                iguais às de `SalesReport.js`/`prototipo-relatorios.html`. */}
                            <Stack
                                direction="row"
                                sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px', bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', mb: 2.25, flexWrap: 'wrap' }}
                            >
                                {[
                                    { icon: ShoppingCart, label: 'Total de Vendas & OS' },
                                    { icon: Heart, label: 'Relevantes' },
                                    { icon: BarChart3, label: 'Gráficos' },
                                ].map((tab, index) => {
                                    const Icon = tab.icon;
                                    const selected = activeTab === index;
                                    return (
                                        <Button
                                            key={tab.label}
                                            onClick={() => handleTabChange(index)}
                                            startIcon={<Icon size={14} strokeWidth={2} />}
                                            sx={{
                                                gap: '7px',
                                                padding: '9px 18px',
                                                lineHeight: 'normal',
                                                borderRadius: '8px',
                                                fontSize: '12.5px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                color: selected ? 'primary.main' : 'text.secondary',
                                                bgcolor: selected ? 'background.paper' : 'transparent',
                                                boxShadow: selected ? (t) => t.customShadows?.sm : 'none',
                                                '&:hover': { bgcolor: selected ? 'background.paper' : 'background.subtle' },
                                            }}
                                        >
                                            {tab.label}
                                        </Button>
                                    );
                                })}
                            </Stack>

                            {/* ===== TAB: TOTAL DE VENDAS & OS ===== */}
                            {activeTab === 0 && (
                                <Card>
                                    <CardContent sx={{ p: 2.25 }}>
                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Total gasto por cliente</Typography>
                                                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                                                    Vendas + ordens de serviço no período, ordenado por valor total
                                                </Typography>
                                            </Box>
                                            {/* Busca por nome: filtro local (não exige gerar). O mesmo
                                                estado também filtra Aniversariantes e Inativos (aba Relevantes). */}
                                            <Tooltip title="Filtra por nome também nas listas de aniversariantes e inativos (aba Relevantes)" arrow>
                                                <TextField
                                                    size="small"
                                                    placeholder="Buscar cliente"
                                                    inputProps={{ 'aria-label': 'Buscar cliente' }}
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    sx={textFieldSx}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search size={15} strokeWidth={2} color={theme.palette.text.disabled} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                        <DefaultTable
                                            colunms={[
                                                { path: 'name', name: 'Cliente', render: (row) => <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{row.name}</Typography> },
                                                { path: 'sales', name: 'Valor vendas', render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{formatBrl(row.sales)}</Typography> },
                                                { path: 'os', name: 'Valor OS', render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{formatBrl(row.os)}</Typography> },
                                                { path: 'total', name: 'Total geral', render: (row) => <Typography sx={{ fontSize: '13px', fontWeight: 800, color: 'success.main' }}>{formatBrl(row.total)}</Typography> },
                                            ]}
                                            data={filteredCustomerRows}
                                            rowPerPage={10}
                                            itemLabel="clientes"
                                            embedded
                                            sx={{ boxShadow: 'none', border: 'none' }}
                                        />
                                    </CardContent>
                                </Card>
                            )}

                            {/* ===== TAB: RELEVANTES ===== */}
                            {activeTab === 1 && (
                                <Stack spacing={2}>
                                    {search.trim() && (
                                        <Box>
                                            <Chip
                                                icon={<Search size={13} strokeWidth={2} />}
                                                label={`Filtrando por nome: "${search.trim()}"`}
                                                size="small"
                                                onDelete={() => setSearch('')}
                                                sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                                            />
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <Card>
                                            <CardContent sx={{ p: 2.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'warning.bg', color: 'warning.main', flexShrink: 0 }}>
                                                        <Gift size={18} strokeWidth={2} />
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Aniversariantes do Mês</Typography>
                                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{currentMonthLabel} — oportunidade de contato e mensagem de parabéns</Typography>
                                                    </Box>
                                                </Box>
                                                <MiniTable
                                                    columns={[
                                                        { key: 'name', label: 'Cliente' },
                                                        { key: 'age', label: 'Idade', align: 'right' },
                                                        { key: 'action', label: '', align: 'right' },
                                                    ]}
                                                    rows={filteredBirthdays}
                                                    emptyTitle="Nenhum aniversariante"
                                                    emptySubtitle="Sem aniversariantes neste mês"
                                                    renderRow={(row, index) => (
                                                        <TableRow key={`${row.name}-${index}`} hover>
                                                            <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.name}</TableCell>
                                                            <TableCell align="right" sx={miniBodyCellSx}>{row.age} anos</TableCell>
                                                            <TableCell align="right" sx={{ ...miniBodyCellSx, whiteSpace: 'nowrap' }}>
                                                                {/* Telefone/nascimento não vêm em `Birthdays` (`report/customer.go`
                                                                    só devolve nome + idade) — ação desabilitada em vez de simular
                                                                    um envio que não existe. */}
                                                                <Tooltip title="Telefone não incluído neste relatório hoje (precisa expor cell_phone em Birthdays)" arrow>
                                                                    <span>
                                                                        <Button size="small" variant="outlined" disabled startIcon={<Send size={13} strokeWidth={2} />} sx={{ fontSize: '11.5px' }}>
                                                                            Mensagem
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                />
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardContent sx={{ p: 2.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'info.bg', color: 'primary.main', flexShrink: 0 }}>
                                                        <Heart size={18} strokeWidth={2} />
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Resumo de Relevância</Typography>
                                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>Clientes que merecem atenção no período</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
                                                    <StatTile icon={<Cake size={20} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" value={<AnimatedNumber value={birthdays.length} format={String} />} label="Aniversariantes" loading={loading} />
                                                    <StatTile icon={<UserMinus size={20} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" value={<AnimatedNumber value={reportData.InactiveCustomers || 0} format={String} />} label="Inativos" loading={loading} />
                                                    <StatTile icon={<UserPlus size={20} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" value={<AnimatedNumber value={reportData.NewCustomers || 0} format={String} />} label="Novos no período" loading={loading} />
                                                </Box>
                                                <MetricRow
                                                    icon={<Heart size={15} strokeWidth={2} />}
                                                    iconBg="secondary.bg"
                                                    iconFg="secondary.main"
                                                    label="Aniversariantes também inativos"
                                                    value={`${birthdaysAlsoInactiveCount} cliente(s) — ótima oportunidade de reengajamento`}
                                                />
                                                <MetricRow
                                                    icon={<UserMinus size={15} strokeWidth={2} />}
                                                    iconBg="info.bg"
                                                    iconFg="primary.main"
                                                    label="Critério de inatividade"
                                                    value="Cadastro com mais de 60 dias e nenhuma venda finalizada ou OS paga até hoje"
                                                />
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    <Card>
                                        <CardContent sx={{ p: 2.25 }}>
                                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Clientes Inativos</Typography>
                                            <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px', mb: 2 }}>
                                                Sem venda finalizada ou OS paga registrada — priorize contato pelos mais antigos
                                            </Typography>
                                            <MiniTable
                                                maxHeight={420}
                                                columns={[
                                                    { key: 'name', label: 'Cliente' },
                                                    { key: 'days', label: 'Sem interação (aprox.)', align: 'right' },
                                                    { key: 'spent', label: 'Total histórico gasto', align: 'right' },
                                                ]}
                                                rows={filteredInactiveCustomers}
                                                emptyTitle="Nenhum cliente inativo"
                                                emptySubtitle="Todos os clientes tiveram interação recente"
                                                renderRow={(row) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.name}</TableCell>
                                                        <TableCell align="right" sx={miniBodyCellSx}>
                                                            {row.approxDays != null ? (
                                                                <Pill label={`~${row.approxDays} dias`} tone={row.approxDays >= 180 ? 'red' : 'amber'} />
                                                            ) : (
                                                                <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.disabled' }}>—</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right" sx={miniBodyCellSx}>
                                                            {row.totalSpent != null ? formatBrl(row.totalSpent) : '—'}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </Stack>
                            )}

                            {/* ===== TAB: GRÁFICOS ===== */}
                            {activeTab === 2 && (
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <DonutPanel
                                            title="Perfil de clientes"
                                            subtitle="Pessoa Física x Pessoa Jurídica (estimado)"
                                            data={[
                                                { id: 0, label: 'Pessoa Física', value: pfCount, displayValue: `${pfPct}%`, color: theme.palette.primary.main },
                                                { id: 1, label: 'Pessoa Jurídica', value: pjCount, displayValue: `${pjPct}%`, color: theme.palette.secondary.main },
                                            ]}
                                            centerValue={countCustomers.toLocaleString('pt-BR')}
                                            centerCaption="clientes"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem clientes cadastrados"
                                        />
                                        <AreaLineChart
                                            dataset={monthlyGrowthData}
                                            xKey="month"
                                            yKey="total"
                                            gradientId="customerReportGrowthGradient"
                                            title="Crescimento da base de clientes"
                                            subtitle={`Total de clientes cadastrados por mês em ${dayjs().year()}`}
                                            loading={loading}
                                            valueSuffix=" cliente(s)"
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'stretch' }}>
                                        <DonutPanel
                                            title="Ativos x Inativos"
                                            data={[
                                                { id: 0, label: 'Ativos', value: activeCount, color: theme.palette.success.main },
                                                { id: 1, label: 'Inativos', value: reportData.InactiveCustomers || 0, color: theme.palette.error.main },
                                            ]}
                                            centerValue={`${activePct}%`}
                                            centerCaption="ativos"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem clientes cadastrados"
                                        />
                                        <CategoryBarChart data={spendBrackets} xKey="faixa" title="Clientes por faixa de gasto" loading={loading} />
                                        {/* "Novos x Inativados por mês" (barras empilhadas) do protótipo não
                                            entrou: a API não expõe nenhuma série mensal de clientes que ficaram
                                            inativos (só o total corrente `InactiveCustomers`), então não há como
                                            montar essa comparação por mês sem um novo endpoint/campo no backend. */}
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <ChartPanel title="Clientes com maior valor gasto" subtitle="Top 5 no período (vendas + OS)" loading={loading}>
                                            <MiniTable
                                                columns={[{ key: 'name', label: 'Cliente' }, { key: 'total', label: 'Total gasto', align: 'right' }]}
                                                rows={top5BySpend}
                                                emptyTitle="Nenhum dado disponível"
                                                emptySubtitle="Sem clientes com valores no período"
                                                renderRow={(row) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.name}</TableCell>
                                                        <TableCell align="right" sx={{ ...miniBodyCellSx, fontWeight: 800, color: 'success.main' }}>{formatBrl(row.total)}</TableCell>
                                                    </TableRow>
                                                )}
                                            />
                                        </ChartPanel>
                                        <ChartPanel title="Clientes com mais vendas" subtitle="Top 5 por quantidade de vendas finalizadas" loading={loading}>
                                            <MiniTable
                                                columns={[{ key: 'name', label: 'Cliente' }, { key: 'count', label: 'Vendas', align: 'right' }]}
                                                rows={top5BySalesCount}
                                                emptyTitle="Nenhum dado disponível"
                                                emptySubtitle="Sem vendas finalizadas no período"
                                                renderRow={(row) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.name}</TableCell>
                                                        <TableCell align="right" sx={miniBodyCellSx}><CountChip value={row.count} /></TableCell>
                                                    </TableRow>
                                                )}
                                            />
                                        </ChartPanel>
                                    </Box>
                                </Stack>
                            )}
                        </>
                    ) : (
                        <Card>
                            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 72, height: 72, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Users size={30} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>Nenhum relatório gerado</Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>
                                    Selecione um período e clique em "Gerar relatório" para visualizar os dados de clientes.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </LocalizationProvider>

            <DialogInfo
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                title="Erro"
                iconType="error"
                content={dialogMessage}
            />
        </PageScaffold>
    );
}
